import { auth } from '@/lib';
import { Info } from '@nosinovacao/fe-icons';
import { Button, cn, useI18n, useInMediaQuery } from '@nosinovacao/nosid-mfe-common';
import { type ErrorComponentProps } from '@tanstack/react-router';
import { getErrorCode } from './utils';

function ErrorMaincontent({
  error,
  errorIdSpaced = false,
}: Readonly<{ error: unknown; errorIdSpaced?: boolean }>) {
  const { t } = useI18n();
  const errorCode = getErrorCode(error);
  const errorIdKey = `Errors.${errorCode}.Id`;
  const errorMessageKey = `Errors.${errorCode}.Message`;
  const errorId = t(errorIdKey);
  const errorMessage = t(errorMessageKey);
  const message =
    errorMessage === errorMessageKey ? t('Errors.DefaultError.Message').toString() : errorMessage;
  const id = errorId === errorIdKey ? t('Errors.DefaultError.Id') : errorId;
  return (
    <main className="grid gap-4">
      <Info width={48} height={48} className="text-woo-pink" />
      <h1 data-tid="title-error" className="font-bold text-nos-neutral-0 font-title-h2">
        {t('Errors.Title')}
      </h1>
      <h2 data-tid="text-error-message" className="text-nos-neutral-200">
        {message}
      </h2>
      <span
        data-tid="text-error-id"
        className={cn(
          'w-fit bg-woo-blue p-2 font-bold text-nos-neutral-0 font-ui-9',
          errorIdSpaced && 'mt-8',
        )}
      >
        {id}
      </span>
    </main>
  );
}

function ErrorFooter({ reset }: Readonly<{ reset: () => void }>) {
  const { t } = useI18n();
  return (
    <footer className={cn('grid gap-4 tablet:grid-cols-[auto_auto] tablet:justify-start')}>
      <Button
        data-tid="button-try-again"
        skin="woo-dark"
        onClick={reset}
        className="row-start-2 tablet:col-start-1 tablet:row-start-1"
      >
        {t('Errors.TryAgain')}
      </Button>
      <Button
        data-tid="button-logout"
        skin="woo-dark"
        variant="secondary"
        onClick={() => auth.logout()}
        className="row-start-1 tablet:col-start-2 tablet:row-start-1"
      >
        {t('Common.Logout')}
      </Button>
    </footer>
  );
}

export function ErrorWooDarkRoot({ error, reset }: Readonly<ErrorComponentProps>) {
  return (
    <div className={cn('grid h-dvh w-dvw place-items-center bg-nos-neutral-1000')}>
      <div className="grid size-full grid-rows-[1fr_auto] gap-10 p-4 pb-8 tablet:h-auto tablet:w-[min(87dvw,46.75rem)] tablet:rounded-lg tablet:border tablet:border-nos-neutral-800 tablet:bg-nos-neutral-900 tablet:p-10 tablet:shadow-3">
        <div className="grid items-center">
          <ErrorMaincontent error={error} />
        </div>
        <ErrorFooter reset={reset} />
      </div>
    </div>
  );
}

export function ErrorWooDarkPage({ error, reset }: Readonly<ErrorComponentProps>) {
  const isDesktop = useInMediaQuery('desktop-md');
  return (
    <div className="inset-0 grid size-full place-items-center desktop-md:absolute">
      <div className="grid size-full gap-6 border border-nos-neutral-800 bg-nos-neutral-900 px-4 py-10 shadow-3 desktop-md:h-auto desktop-md:w-[min(95%,46.75rem)] desktop-md:gap-10 desktop-md:rounded-lg">
        <ErrorMaincontent error={error} errorIdSpaced={!isDesktop} />
        <ErrorFooter reset={reset} />
      </div>
    </div>
  );
}

export function ErrorWooDarkSidePanel({ error, reset }: Readonly<ErrorComponentProps>) {
  return (
    <div className={cn('grid size-full place-items-center')}>
      <div className="grid size-full grid-rows-[1fr_auto] gap-10 pb-8 tablet:pb-0">
        <div className="grid items-center">
          <ErrorMaincontent error={error} />
        </div>
        <ErrorFooter reset={reset} />
      </div>
    </div>
  );
}
